import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import * as XLSX from 'xlsx';
import './SellerDomesticHvi.css';
import BuyerDomesticHvi from './BuyerDomesticHvi';
import api from '../api';

const SellerDomesticHvi = () => {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);
  const [searchParams] = useSearchParams();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const [viewMode, setViewMode] = useState('seller'); // 'seller' or 'buyer'
  const [currentProduct, setCurrentProduct] = useState(searchParams.get('product') || 'cotton');
  const [currentSubType, setCurrentSubType] = useState(searchParams.get('subType') || 'hvi');

  const productOptions = [
    {
      id: 'cotton', name: 'Cotton', icon: '🌱',
      subTypes: [
        { id: 'hvi', name: 'Domestic Cotton with HVI' },
        { id: 'domestic-desc', name: 'Domestic Cotton - Description' },
        { id: 'import-desc', name: 'Import Cotton - Description' },
        { id: 'import-recap', name: 'Import Cotton - Recaps' },
        { id: 'cert', name: 'Cotton Certification' },
        { id: 'waste', name: 'Cotton Waste' }
      ]
    },
    {
      id: 'viscose', name: 'Viscose', icon: '🌿',
      subTypes: [
        { id: 'domestic', name: 'Domestic Viscose' },
        { id: 'imported', name: 'Imported Viscose' }
      ]
    },
    {
      id: 'polyester', name: 'Polyester', icon: '🧪',
      subTypes: [
        { id: 'domestic', name: 'Domestic Polyester' },
        { id: 'imported', name: 'Imported Polyester' }
      ]
    },
    {
      id: 'cotton-waste', name: 'Cotton Waste', icon: '♻️',
      subTypes: [
        { id: 'comber', name: 'Comber' },
        { id: 'fs', name: 'FS' }
      ]
    },
    {
      id: 'yarn', name: 'Yarn', icon: '🧵',
      subTypes: [
        { id: 'warp', name: 'Warp' },
        { id: 'weft', name: 'Weft' }
      ]
    },
    {
      id: 'fabric', name: 'Fabric', icon: '👕',
      subTypes: [
        { id: 'cotton-fabric', name: 'Cotton Fabric' },
        { id: 'blended', name: 'Blended' }
      ]
    },
    {
      id: 'spare-parts', name: 'Spare Parts', icon: '⚙️',
      subTypes: [
        { id: 'new', name: 'New' },
        { id: 'used', name: 'Used' }
      ]
    },
    {
      id: 'units', name: 'Real Estate', icon: '🏭',
      subTypes: [
        { id: 'sell', name: 'Sell' },
        { id: 'conversion', name: 'Conversion' },
        { id: 'lease', name: 'Lease' }
      ]
    }
  ];

  // --- Dropdown Options ---
  const origins = ['USA (Pima)', 'USA (Upland)', 'Brazil', 'West Africa', 'Australia', 'Egypt (Giza)', 'CIS', 'Others'];
  const growths = ['Pima', 'Giza', 'Upland', 'Others'];
  const wasteTypes = ['Comber Noil', 'Flat Strip', 'Lickerin', 'Yellow Picker', 'Others'];
  const cropYears = ['2023-24', '2024-25', '2025-26', '2026-27', '2027-28', 'Others'];
  const states = ['Andhra Pradesh', 'Gujarat', 'Haryana', 'Karnataka', 'Maharashtra', 'Madhya Pradesh', 'Odisha', 'Punjab', 'Rajasthan', 'Tamil Nadu', 'Telangana', 'Others'];
  const stations = ['Raichur', 'Adilabad', 'Warangal', 'Khammam', 'Nagpur', 'Kadi', 'Rajkot', 'Others'];
  const warehouses = ['Warehouse A, Location X', 'Warehouse B, Location Y', 'Gin Yard', 'Others'];
  const labs = ['CAI Hubli', 'TEXTILE LAB A', 'GOVT LAB B', 'Others'];
  const [certificationsList, setCertificationsList] = useState([
    { id: 'BCI', name: 'BCI', premium: 700 },
    { id: 'RegenAgri', name: 'RegenAgri', premium: 1600 },
    { id: 'CMiA', name: 'CMiA', premium: 800 },
    { id: 'Npop', name: 'Npop', premium: 5350 },
    { id: 'Nop', name: 'Nop', premium: 6400 },
    { id: 'cert6', name: 'Certificate 6', premium: 0 },
    { id: 'cert7', name: 'Certificate 7', premium: 0 },
    { id: 'cert8', name: 'Certificate 8', premium: 0 },
    { id: 'cert9', name: 'Certificate 9', premium: 0 },
    { id: 'cert10', name: 'Certificate 10', premium: 0 },
    { id: 'cert11', name: 'Certificate 11', premium: 0 },
    { id: 'cert12', name: 'Certificate 12', premium: 0 },
    { id: 'cert13', name: 'Certificate 13', premium: 0 },
    { id: 'cert14', name: 'Certificate 14', premium: 0 },
    { id: 'cert15', name: 'Certificate 15', premium: 0 },
  ]);

  const [customCertName, setCustomCertName] = useState('');
  const [customCertPremium, setCustomCertPremium] = useState('');

  const handleAddCustomCertType = () => {
    if (!customCertName || !customCertPremium) {
      alert('Please enter both Certificate Name and Premium');
      return;
    }
    const newId = customCertName.replace(/\s+/g, '_');
    if (certificationsList.some(c => c.id.toLowerCase() === newId.toLowerCase())) {
      alert('This certificate already exists');
      return;
    }
    const newCert = {
      id: newId,
      name: customCertName,
      premium: Number(customCertPremium)
    };
    setCertificationsList(prev => [...prev, newCert]);
    setCustomCertName('');
    setCustomCertPremium('');
  };

  const initialEntryState = {
    cropYear: '', state: '', station: '', lotNumber: '', warehouse: '', labName: '',
    origin: '', growth: '', wasteType: '', trash: '', moisture: '',
    sci: '', mic: '', micRange: '', sl: '', ur: '', sf: '', str: '', elg: '', rd: '', plusB: '', cgrd: '',
    validDate: '', closingTime: '12:00', offerQty: '',
    offerPrice: '', inrCandy: '', uscLbs: '', cifIndiaBasis: '',
    certification: 'No',
    selectedCerts: [],
    remarks: '',
    // Import Specific
    leaf: '', staple: '', gpt: '', gptMinAvg: '', shipFrom: '', shipment: '', eta: '', coverMonth: 'K26', colourGrade: '',
    recapFileName: '',
    recapFile: null,
    // Waste Specific
    blendWithMMF: 'No', contaminationFree: 'No', clothCovered: 'No',
    // Dynamic custom parameters for non-Cotton products
    customParams: [],
    // Logic
    termsAccepted: false,
    securityPin: '',
    // Custom values for "Others"
    custom_cropYear: '', custom_state: '', custom_station: '', custom_warehouse: '', custom_labName: '',
    custom_origin: '', custom_growth: '', custom_wasteType: '', custom_trash: '', custom_moisture: '',
    custom_sci: '', custom_mic: '', custom_micRange: '', custom_sl: '', custom_ur: '', custom_sf: '',
    custom_str: '', custom_elg: '', custom_rd: '', custom_plusB: '', custom_cgrd: '', custom_offerPrice: '',
    sellingType: 'current',
    startDate: '',
    endDate: '',
    certificateFile: null,
    testReportFile: null,
    excelFile: null,
    sellerName: localStorage.getItem('userEmail') || 'Seller A',
    status: 'Live',
    pricingType: 'Flat',
    basisPoints: 1500,
    pricingPercentage: '0%',
    transportService: 'EX.Whs',
    forState: '',
    offerQtyTerms: '',
    hasTestReport: 'No',
    forexPrem: '',
    latePaymentCharge: '',
    forwardOffers: [],
    validDate: ''
  };

  const [currentEntry, setCurrentEntry] = useState(initialEntryState);
  const [sellingType, setSellingType] = useState('current'); // 'current' or 'forward'
  const [entryMode, setEntryMode] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [savedEntries, setSavedEntries] = useState(() => {
    const saved = localStorage.getItem('sellerEntries');
    return saved ? JSON.parse(saved) : [];
  });

  const [managedListingId, setManagedListingId] = useState(null);
  const [activeBids, setActiveBids] = useState([]);
  const [now, setNow] = useState(new Date());
  const [showTerms, setShowTerms] = useState(false);
  const [showCertModal, setShowCertModal] = useState(false);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [forwardOffersTemp, setForwardOffersTemp] = useState([]);
  const [pinActionData, setPinActionData] = useState(null); // { bidId, action, additionalData }

  const [cottonRates, setCottonRates] = useState({
    USDINR: 83.25,
    H27: 81.16,
    K26: 82.45,
    N26: 76.17,
    Z26: 79.80
  });

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await api.get('/listings/cotton-rates', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data) {
          setCottonRates(res.data);
        }
      } catch (e) {
        console.error('Failed to fetch cotton rates, using default fallbacks.', e);
      }
    };
    fetchRates();
  }, []);

  useEffect(() => {
    if (currentProduct === 'cotton' && currentEntry.pricingType === 'Basis') {
      // coverMonth is a key like 'K26', 'N26', 'V26', 'Z26', 'H27'
      // ICE price is in US cents per pound (¢/lb)
      const iceVal = parseFloat(cottonRates[currentEntry.coverMonth] || 0);
      // basisPoints are in points (1 pt = 0.01 ¢/lb), divide by 100
      const basisVal = parseFloat(currentEntry.basisPoints || 0) / 100;
      // forex is USD/INR rate; forexPrem is additive INR premium in Paise, so divide by 100 to get Rupees
      const fxVal = parseFloat(cottonRates.USDINR || 83.25);
      const forexPremVal = parseFloat(currentEntry.forexPrem || 0) / 100;

      // Formula: (ICE + basis/100) × 7.84 × (forex + forexPrem/100)
      // 7.84 converts ¢/lb to ₹/candy (2.2046 lbs/kg × 356 kg/candy ÷ 100)
      const calculatedPrice = Math.round(
        (iceVal + basisVal) * 7.84 * (fxVal + forexPremVal)
      );

      if (currentEntry.offerPrice !== String(calculatedPrice)) {
        setCurrentEntry(prev => ({
          ...prev,
          offerPrice: String(calculatedPrice)
        }));
      }
    }
  }, [
    currentProduct,
    currentEntry.coverMonth,
    currentEntry.basisPoints,
    currentEntry.forexPrem,
    cottonRates
  ]);

  const isCertOrWaste = (currentProduct === 'cotton' && (currentSubType === 'cert' || currentSubType === 'waste')) || currentProduct === 'cotton-waste';

  useEffect(() => {
    if (currentProduct === 'cotton' && currentSubType === 'cert') {
      if (sellingType !== 'current') {
        setSellingType('current');
      }
    }
  }, [currentProduct, currentSubType, sellingType]);

  useEffect(() => {
    if (isCertOrWaste) {
      if (currentEntry.pricingType !== 'Flat') {
        setCurrentEntry(prev => ({ ...prev, pricingType: 'Flat' }));
      }
    }
  }, [currentProduct, currentSubType, isCertOrWaste, currentEntry.pricingType]);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const calculateTimeLeft = (validDate, closingTime) => {
    if (!validDate) return 'No Limit';
    const time = closingTime || '12:00';
    const expiry = new Date(`${validDate}T${time}`);
    const diff = expiry - now;

    if (diff <= 0) return 'EXPIRED';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (days > 0) return `${days}d ${hours}h`;
    return `${hours}h ${minutes}m ${seconds}s`;
  };

  // Helper: compute calendar months between two YYYY-MM-DD dates, e.g. "Jul-2026"
  const getMonthsBetween = (startDate, endDate) => {
    if (!startDate || !endDate) return [];
    const s = new Date(startDate), e = new Date(endDate);
    if (isNaN(s.getTime()) || isNaN(e.getTime()) || s > e) return [];
    const NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const months = [];
    const cur = new Date(s.getFullYear(), s.getMonth(), 1);
    const endM = new Date(e.getFullYear(), e.getMonth(), 1);
    while (cur <= endM) {
      months.push(`${NAMES[cur.getMonth()]}-${cur.getFullYear()}`);
      cur.setMonth(cur.getMonth() + 1);
    }
    return months;
  };

  // Re-build the monthlyPrices grid whenever start/end dates change in forward selling
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (sellingType !== 'forward' || !currentEntry.startDate || !currentEntry.endDate) return;
    const months = getMonthsBetween(currentEntry.startDate, currentEntry.endDate);
    setCurrentEntry(prev => {
      const next = {};
      months.forEach(m => { next[m] = (prev.monthlyPrices || {})[m] ?? ''; });
      if (JSON.stringify(Object.keys(next)) === JSON.stringify(Object.keys(prev.monthlyPrices || {}))) return prev;
      return { ...prev, monthlyPrices: next };
    });
  }, [currentEntry.startDate, currentEntry.endDate, sellingType]);

  // Sync bids from localStorage whenever the managed listing changes
  useEffect(() => {
    const fetchBids = () => {
      if (managedListingId) {
        const allBids = JSON.parse(localStorage.getItem('bids') || '[]');
        const filtered = allBids.filter(b => b.listingId === managedListingId);
        setActiveBids(filtered);

        // Mark bids as seen when the modal opens
        if (filtered.some(b => !b.seen)) {
          const updatedBids = allBids.map(b => b.listingId === managedListingId ? { ...b, seen: true } : b);
          localStorage.setItem('bids', JSON.stringify(updatedBids));
          window.dispatchEvent(new Event('bidsUpdated'));
        }
      }
    };

    fetchBids(); // Load initially

    // Listen for storage changes to reflect new bids immediately
    window.addEventListener('storage', fetchBids);

    // Custom event for same-window updates
    window.addEventListener('bidsUpdated', fetchBids);

    return () => {
      window.removeEventListener('storage', fetchBids);
      window.removeEventListener('bidsUpdated', fetchBids);
    };
  }, [managedListingId]);

  useEffect(() => {
    localStorage.setItem('sellerEntries', JSON.stringify(savedEntries));
  }, [savedEntries]);

  const handleProductSelect = (productId) => {
    const product = productOptions.find(p => p.id === productId);
    setCurrentProduct(productId);
    const defaultSubType = product.subTypes ? product.subTypes[0].id : '';
    setCurrentSubType(defaultSubType);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('product', productId);
    if (defaultSubType) newParams.set('subType', defaultSubType);
    navigate(`/seller/domestic-hvi?${newParams.toString()}`, { replace: true });
  };

  const handleSubTypeSelect = (subId) => {
    setCurrentSubType(subId);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('subType', subId);
    navigate(`/seller/domestic-hvi?${newParams.toString()}`, { replace: true });
  };

  const confirmPinAction = async (pin) => {
    if (!pin) {
      alert('Please enter your 4-digit Security PIN');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const pinResponse = await api.post('/user/verify-pin', {
        pin: pin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!pinResponse.data.success) {
        alert('Invalid Security PIN');
        return;
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to verify Security PIN');
      return;
    }

    const { bidId, action, additionalData } = pinActionData;
    const allBids = JSON.parse(localStorage.getItem('bids') || '[]');
    const updatedBids = allBids.map(bid => {
      if (bid.id === bidId) {
        const updatedBid = { ...bid, status: action };
        if (action === 'Countered' && additionalData) {
          updatedBid.counterProductPrice = additionalData.counterProductPrice;
          updatedBid.counterCertPrices = additionalData.counterCertPrices;
        }
        return updatedBid;
      }
      return bid;
    });
    localStorage.setItem('bids', JSON.stringify(updatedBids));

    setActiveBids(prev => prev.map(bid => {
      if (bid.id === bidId) {
        const updatedBid = { ...bid, status: action };
        if (action === 'Countered' && additionalData) {
          updatedBid.counterProductPrice = additionalData.counterProductPrice;
          updatedBid.counterCertPrices = additionalData.counterCertPrices;
        }
        return updatedBid;
      }
      return bid;
    }));

    alert(`Bid ${action} successfully!`);
    setPinActionData(null);
    setManagedListingId(null);
    window.dispatchEvent(new Event('bidsUpdated'));
  };

  const handleBidAction = (bidId, action) => {
    if (action === 'Countering') {
      setActiveBids(prev => prev.map(bid =>
        bid.id === bidId ? { ...bid, status: 'Countering' } : bid
      ));
    } else if (action === 'Pending') {
      const allBids = JSON.parse(localStorage.getItem('bids') || '[]');
      const originalBid = allBids.find(b => b.id === bidId);
      setActiveBids(prev => prev.map(bid =>
        bid.id === bidId ? { ...bid, status: originalBid ? originalBid.status : 'Pending' } : bid
      ));
    } else {
      setPinActionData({ bidId, action });
    }
  };

  const handleCounterPriceChange = (bidId, field, value) => {
    setActiveBids(prev => prev.map(bid =>
      bid.id === bidId ? { ...bid, [field]: value } : bid
    ));
  };

  const handleCounterCertPriceChange = (bidId, certId, value) => {
    setActiveBids(prev => prev.map(bid => {
      if (bid.id === bidId) {
        const counterCertPrices = { ...(bid.counterCertPrices || {}) };
        counterCertPrices[certId] = value === '' ? '' : parseFloat(value);
        return { ...bid, counterCertPrices };
      }
      return bid;
    }));
  };

  const handleInputChange = (field, value) => {
    setCurrentEntry(prev => {
      const updates = { ...prev, [field]: value };
      if (field === 'hasTestReport' && value === 'No') {
        updates.testReportFile = null;
      }
      if (field === 'transportService' && value === 'EX.Whs') {
        updates.forState = '';
      }
      if (field === 'offerQtyTerms' && !value) {
        updates.offerQty = '';
      }
      return updates;
    });
  };

  const handleExcelUpload = (file) => {
    if (!file) return;
    
    handleInputChange('excelFile', file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        if (rows.length < 2) {
          alert('Excel sheet has no data rows.');
          return;
        }
        
        const headers = rows[0].map(h => String(h || '').trim().toLowerCase());
        const values = rows[1];
        
        const updates = {};
        
        const assignValue = (field, customField, allowedOptions, val) => {
          if (val === undefined || val === null || val === '') return;
          const strVal = String(val).trim();
          if (allowedOptions.map(o => String(o).toLowerCase()).includes(strVal.toLowerCase())) {
            const exactMatch = allowedOptions.find(o => String(o).toLowerCase() === strVal.toLowerCase());
            updates[field] = exactMatch;
          } else {
            updates[field] = 'Others';
            updates[customField] = strVal;
          }
        };

        const isHeaderAllowed = (headerName) => {
          const commonPrimary = [
            'crop year', 'cropyear', 'offer qty', 'offerqty', 'quantity', 'qty',
            'offer price', 'offerprice', 'price', 'remarks'
          ];
          if (commonPrimary.includes(headerName)) return true;

          if (currentSubType === 'hvi') {
            return [
              'state', 'station', 'station/zone', 'warehouse location', 'warehouse', 'name of the lab', 'lab', 'lab name',
              'lot number', 'lot no', 'lotno', 'lot', 'bale no', 'baleno',
              'sci', 'mic', 'sl', 'sl (mm)', 'sl(mm)', 'ur', 'ur (%)', 'ur%',
              'sf', 'sf (%)', 'sf%', 'str', 'str (g/tex)', 'str(g/tex)',
              'elg', 'elg (%)', 'elg%', 'rd', '+b', 'plusb', 'cgrd', 'cg',
              'colour grade', 'colourgrade'
            ].includes(headerName);
          }
          if (currentSubType === 'domestic-desc') {
            return [
              'state', 'station', 'station/zone',
              'mic min', 'micmin', 'mic max', 'micmax',
              'sl min', 'slmin', 'sl max', 'slmax',
              'moisture min', 'moisturemin', 'moisture max', 'moisturemax',
              'trash min', 'trashmin', 'trash max', 'trashmax',
              'str min', 'strmin', 'str max', 'strmax',
              'rd min', 'rdmin', 'rd max', 'rdmax'
            ].includes(headerName);
          }
          if (currentSubType === 'import-desc' || currentSubType === 'import-recap') {
            return [
              'origin', 'growth',
              'colour grade', 'colourgrade', 'cg',
              'leaf min', 'leafmin', 'leaf max', 'leafmax',
              'staple min', 'staplemin', 'staple max', 'staplemax',
              'mic min', 'micmin', 'mic max', 'micmax',
              'gpt min', 'gptmin', 'gpt max', 'gptmax',
              'gpt min/avg min', 'gptminavgmin', 'gpt min/avg max', 'gptminavgmax',
              'ship from', 'shipment', 'eta', 'cover month', 'recap name', 'recap/ref name'
            ].includes(headerName);
          }
          if (currentSubType === 'waste') {
            return [
              'state', 'station', 'station/zone',
              'lot number', 'lot no', 'lotno', 'lot', 'bale no', 'baleno',
              'type of waste', 'waste type', 'sl', 'sl (mm)', 'sl(mm)', 'moisture',
              'blend with mmf', 'contamination free', 'cloth covered'
            ].includes(headerName);
          }
          return false;
        };

        headers.forEach((header, index) => {
          if (!isHeaderAllowed(header)) return;
          const val = values[index];
          if (val === undefined || val === null || val === '') return;

          switch (header) {
            case 'sci':
              assignValue('sci', 'custom_sci', ['120', '125', '130', 'Others'], val);
              break;
            case 'mic':
              assignValue('mic', 'custom_mic', ['3.4', '3.8', '4.2', '4.5', '4.8', 'Others'], val);
              break;
            case 'sl':
            case 'sl (mm)':
            case 'sl(mm)':
              if (currentSubType === 'hvi') {
                assignValue('sl', 'custom_sl', ['28.5', '29.0', '29.5', '30.0', '31.0', 'Others'], val);
              } else if (currentSubType === 'waste') {
                assignValue('sl', 'custom_sl', ['12', '14', '16', 'Others'], val);
              }
              break;
            case 'ur':
            case 'ur (%)':
            case 'ur%':
              assignValue('ur', 'custom_ur', ['80%', '81%', '82%', '83%', '84%', 'Others'], val);
              break;
            case 'sf':
            case 'sf (%)':
            case 'sf%':
              assignValue('sf', 'custom_sf', ['5.0%', '6.0%', '7.0%', '8.0%', '9.0%', 'Others'], val);
              break;
            case 'str':
            case 'str (g/tex)':
            case 'str(g/tex)':
              assignValue('str', 'custom_str', ['28.0', '29.0', '30.0', '31.0', '32.0', 'Others'], val);
              break;
            case 'elg':
            case 'elg (%)':
            case 'elg%':
              assignValue('elg', 'custom_elg', ['5.5', '6.0', '6.5', 'Others'], val);
              break;
            case 'rd':
              assignValue('rd', 'custom_rd', ['70', '72', '74', '76', '78', '75', '80', 'Others'], val);
              break;
            case '+b':
            case 'plusb':
              assignValue('plusB', 'custom_plusB', ['8.0', '9.0', '10.0', '11.0', 'Others'], val);
              break;
            case 'cgrd':
            case 'cg':
            case 'colour grade':
            case 'colourgrade':
              if (currentSubType === 'hvi') {
                assignValue('cgrd', 'custom_cgrd', ['11-1', '21-1', '31-1', '41-1', 'Others'], val);
              } else if (currentSubType.includes('import')) {
                assignValue('colourGrade', 'custom_cgrd', ['11-1', '21-1', 'Others'], val);
              }
              break;
            case 'crop year':
            case 'cropyear':
              assignValue('cropYear', 'custom_cropYear', ['2023-24', '2024-25', '2025-26', '2026-27', '2027-28', 'Others'], val);
              break;
            case 'state':
              assignValue('state', 'custom_state', ['Andhra Pradesh', 'Gujarat', 'Haryana', 'Karnataka', 'Maharashtra', 'Madhya Pradesh', 'Odisha', 'Punjab', 'Rajasthan', 'Tamil Nadu', 'Telangana', 'Others'], val);
              break;
            case 'station':
            case 'station/zone':
              assignValue('station', 'custom_station', ['Raichur', 'Adilabad', 'Warangal', 'Khammam', 'Nagpur', 'Kadi', 'Rajkot', 'Others'], val);
              break;
            case 'warehouse location':
            case 'warehouse':
              assignValue('warehouse', 'custom_warehouse', ['Warehouse A, Location X', 'Warehouse B, Location Y', 'Gin Yard', 'Others'], val);
              break;
            case 'name of the lab':
            case 'lab':
            case 'lab name':
              assignValue('labName', 'custom_labName', ['CAI Hubli', 'TEXTILE LAB A', 'GOVT LAB B', 'Others'], val);
              break;
            case 'offer qty':
            case 'offerqty':
            case 'quantity':
            case 'qty':
              updates['offerQty'] = String(val);
              break;
            case 'lot number':
            case 'lot no':
            case 'lotno':
            case 'lot':
            case 'bale no':
            case 'baleno':
              updates['lotNumber'] = String(val);
              break;
            case 'offer price':
            case 'offerprice':
            case 'price':
              assignValue('offerPrice', 'custom_offerPrice', ['55000', '58000', '60000', 'Others'], val);
              break;
            case 'remarks':
              updates['remarks'] = String(val);
              break;
            case 'mic min':
            case 'micmin':
              updates['micMin'] = String(val);
              break;
            case 'mic max':
            case 'micmax':
              updates['micMax'] = String(val);
              break;
            case 'sl min':
            case 'slmin':
              updates['slMin'] = String(val);
              break;
            case 'sl max':
            case 'slmax':
              updates['slMax'] = String(val);
              break;
            case 'moisture min':
            case 'moisturemin':
              updates['moistureMin'] = String(val);
              break;
            case 'moisture max':
            case 'moisturemax':
              updates['moistureMax'] = String(val);
              break;
            case 'trash min':
            case 'trashmin':
              updates['trashMin'] = String(val);
              break;
            case 'trash max':
            case 'trashmax':
              updates['trashMax'] = String(val);
              break;
            case 'str min':
            case 'strmin':
              updates['strMin'] = String(val);
              break;
            case 'str max':
            case 'strmax':
              updates['strMax'] = String(val);
              break;
            case 'rd min':
            case 'rdmin':
              updates['rdMin'] = String(val);
              break;
            case 'rd max':
            case 'rdmax':
              updates['rdMax'] = String(val);
              break;
            case 'leaf min':
            case 'leafmin':
              updates['leafMin'] = String(val);
              break;
            case 'leaf max':
            case 'leafmax':
              updates['leafMax'] = String(val);
              break;
            case 'staple min':
            case 'staplemin':
              updates['stapleMin'] = String(val);
              break;
            case 'staple max':
            case 'staplemax':
              updates['stapleMax'] = String(val);
              break;
            case 'gpt min':
            case 'gptmin':
              updates['gptMin'] = String(val);
              break;
            case 'gpt max':
            case 'gptmax':
              updates['gptMax'] = String(val);
              break;
            case 'gpt min/avg min':
            case 'gptminavgmin':
              updates['gptMinAvgMin'] = String(val);
              break;
            case 'gpt min/avg max':
            case 'gptminavgmax':
              updates['gptMinAvgMax'] = String(val);
              break;
            case 'origin':
              assignValue('origin', 'custom_origin', ['USA (Pima)', 'USA (Upland)', 'Brazil', 'West Africa', 'Australia', 'Egypt (Giza)', 'CIS', 'Others'], val);
              break;
            case 'growth':
              assignValue('growth', 'custom_growth', ['Pima', 'Giza', 'Upland', 'Others'], val);
              break;
            case 'type of waste':
            case 'waste type':
            case 'wastetype':
              assignValue('wasteType', 'custom_wasteType', ['Comber Noil', 'Flat Strip', 'Lickerin', 'Yellow Picker', 'Others'], val);
              break;
            case 'moisture':
            case 'moisture (%)':
            case 'moisture%':
              if (currentSubType === 'waste') {
                assignValue('moisture', 'custom_moisture', ['8%', '9%', '10%', 'Others'], val);
              }
              break;
            case 'blend with mmf':
            case 'blendwithmmf':
              if (String(val).toLowerCase() === 'yes' || String(val).toLowerCase() === 'y') {
                updates['blendWithMMF'] = 'Yes';
              } else {
                updates['blendWithMMF'] = 'No';
              }
              break;
            case 'contamination free':
            case 'contaminationfree':
              if (String(val).toLowerCase() === 'yes' || String(val).toLowerCase() === 'y') {
                updates['contaminationFree'] = 'Yes';
              } else {
                updates['contaminationFree'] = 'No';
              }
              break;
            case 'cloth covered bales':
            case 'clothcoveredbales':
            case 'cloth covered':
            case 'clothcovered':
              if (String(val).toLowerCase() === 'yes' || String(val).toLowerCase() === 'y') {
                updates['clothCovered'] = 'Yes';
              } else {
                updates['clothCovered'] = 'No';
              }
              break;
            case 'ship from':
            case 'shipfrom':
              updates['shipFrom'] = String(val);
              break;
            case 'shipment':
              updates['shipment'] = String(val);
              break;
            case 'eta':
            case 'eta of tsp':
            case 'etatsp':
              updates['eta'] = String(val);
              break;
            case 'cover month':
            case 'covermonth':
              updates['coverMonth'] = String(val);
              break;
            case 'recap name':
            case 'recap/ref name':
            case 'recapfilename':
              updates['recapFileName'] = String(val);
              break;
            default:
              break;
          }
        });
        
        if (Object.keys(updates).length > 0) {
          setCurrentEntry({
            ...initialEntryState,
            sellingType: sellingType,
            ...updates
          });
          alert(`Successfully filled ${Object.keys(updates).length} parameters from Excel!`);
        } else {
          alert('Could not match any standard technical parameter headers in the uploaded Excel.');
        }
      } catch (err) {
        console.error('Error parsing Excel file:', err);
        alert('Failed to parse the Excel file. Make sure it is a valid format.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleCustomCertToggle = (certId, defaultPremium) => {
    const isSelected = currentEntry.selectedCerts.some(c => c.id === certId);
    if (isSelected) {
      setCurrentEntry(prev => ({
        ...prev,
        selectedCerts: prev.selectedCerts.filter(c => c.id !== certId)
      }));
    } else {
      const cert = certificationsList.find(c => c.id === certId);
      setCurrentEntry(prev => ({
        ...prev,
        selectedCerts: [...prev.selectedCerts, { id: certId, name: cert.name, premium: Number(defaultPremium) }]
      }));
    }
  };

  const handleCustomCertPremiumChange = (certId, value) => {
    const numVal = value === '' ? '' : Number(value);
    setCurrentEntry(prev => ({
      ...prev,
      selectedCerts: prev.selectedCerts.map(c =>
        c.id === certId ? { ...c, premium: numVal } : c
      )
    }));
  };

  const calculateTotalPremium = () => {
    return currentEntry.selectedCerts.reduce((acc, curr) => acc + (Number(curr.premium) || 0), 0);
  };

  const saveEntry = async () => {
    if (!currentEntry.termsAccepted) {
      alert('Please accept the Terms & Conditions');
      return;
    }
    if (!currentEntry.securityPin) {
      alert(editingId ? 'Please enter your Security PIN to update lot details' : 'Please enter your Security PIN to publish');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const pinResponse = await api.post('/user/verify-pin', {
        pin: currentEntry.securityPin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!pinResponse.data.success) {
        alert('Invalid Security PIN');
        return;
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to verify Security PIN');
      return;
    }

    const processedEntry = { ...currentEntry };
    Object.keys(processedEntry).forEach(key => {
      if (processedEntry[key] === 'Others' && processedEntry[`custom_${key}`]) {
        processedEntry[key] = processedEntry[`custom_${key}`];
      }
    });

    if (editingId) {
      setSavedEntries(prev => prev.map(item => {
        if (item.id === editingId) {
          return {
            ...item,
            ...processedEntry,
            totalPremium: calculateTotalPremium(),
            timestamp: new Date().toISOString()
          };
        }
        return item;
      }));
      setCurrentEntry({ ...initialEntryState, sellingType: sellingType });
      setEditingId(null);
      setEntryMode(null);
      alert('Lot details updated successfully!');
    } else {
      const newEntry = {
        ...processedEntry,
        id: Date.now(),
        totalPremium: calculateTotalPremium(),
        timestamp: new Date().toISOString(),
        productType: currentProduct,
        subType: currentSubType,
        sellingType: sellingType
      };
      setSavedEntries(prev => [newEntry, ...prev]);
      setCurrentEntry({ ...initialEntryState, sellingType: sellingType });
      setEntryMode(null);
      alert('Listing Published to Marketplace Successfully!');
    }
  };

  const handleStartEdit = (entry) => {
    const entryCopy = { ...entry };
    const dropdownsConfig = {
      sci: ['120', '125', '130'],
      mic: ['3.4', '3.8', '4.2', '4.5', '4.8'],
      sl: ['28.5', '29.0', '29.5', '30.0', '31.0', '12', '14', '16'],
      ur: ['80%', '81%', '82%', '83%', '84%'],
      sf: ['5.0%', '6.0%', '7.0%', '8.0%', '9.0%'],
      str: ['28.0', '29.0', '30.0', '31.0', '32.0'],
      elg: ['5.5', '6.0', '6.5'],
      rd: ['70', '72', '74', '76', '78'],
      plusB: ['8.0', '9.0', '10.0', '11.0'],
      cgrd: ['11-1', '21-1', '31-1', '41-1'],
      cropYear: ['2023-24', '2024-25', '2025-26', '2026-27', '2027-28'],
      state: ['Andhra Pradesh', 'Gujarat', 'Haryana', 'Karnataka', 'Maharashtra', 'Madhya Pradesh', 'Odisha', 'Punjab', 'Rajasthan', 'Tamil Nadu', 'Telangana'],
      station: ['Raichur', 'Adilabad', 'Warangal', 'Khammam', 'Nagpur', 'Kadi', 'Rajkot'],
      warehouse: ['Warehouse A, Location X', 'Warehouse B, Location Y', 'Gin Yard'],
      labName: ['CAI Hubli', 'TEXTILE LAB A', 'GOVT LAB B'],
      origin: ['USA (Pima)', 'USA (Upland)', 'Brazil', 'West Africa', 'Australia', 'Egypt (Giza)', 'CIS'],
      growth: ['Pima', 'Giza', 'Upland'],
      colourGrade: ['11-1', '21-1'],
      wasteType: ['Comber Noil', 'Flat Strip', 'Lickerin', 'Yellow Picker'],
      moisture: ['8%', '9%', '10%']
    };

    // Only apply offerPrice dropdown mapping for non-cotton products (cotton uses direct number input)
    if (entry.productType !== 'cotton') {
      dropdownsConfig.offerPrice = ['55000', '58000', '60000'];
    }

    Object.keys(dropdownsConfig).forEach(field => {
      const val = entryCopy[field];
      if (val) {
        const allowed = dropdownsConfig[field];
        if (!allowed.includes(String(val))) {
          entryCopy[field] = 'Others';
          const customKey = field === 'colourGrade' ? 'custom_cgrd' : `custom_${field}`;
          entryCopy[customKey] = String(val);
        }
      }
    });

    let defaultTerms = entry.offerQtyTerms;
    // Normalize old label formats to shorter ones
    if (defaultTerms === 'Terms of BALS') defaultTerms = 'Bales';
    if (defaultTerms === 'BALS') defaultTerms = 'Bales';
    if (defaultTerms === 'Terms of MT') defaultTerms = 'MT';
    if (!defaultTerms && entry.offerQty) {
      if (entry.subType?.includes('import')) {
        defaultTerms = 'MT';
      } else {
        defaultTerms = 'Bales';
      }
    }
    entryCopy.offerQtyTerms = defaultTerms || '';
    entryCopy.hasTestReport = entry.testReportFile || entry.hasTestReport === 'Yes' ? 'Yes' : 'No';
    entryCopy.forState = entry.forState || '';

    setCurrentEntry({
      ...initialEntryState,
      ...entryCopy,
      monthlyPrices: entry.monthlyPrices || {},
      termsAccepted: true,
      securityPin: ''
    });
    setSellingType(entry.sellingType || 'current');
    
    setEditingId(entry.id);
    setEntryMode('manual');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  const hasUnseenBids = (listingId) => {
    const allBids = JSON.parse(localStorage.getItem('bids') || '[]');
    return allBids.some(b => b.listingId === listingId && !b.seen);
  };

  const getListingStatus = (entry) => {
    const dateToCheck = entry.sellingType === 'forward' ? entry.endDate : entry.validDate;
    const isExpired = calculateTimeLeft(dateToCheck, entry.closingTime) === 'EXPIRED';
    const allBids = JSON.parse(localStorage.getItem('bids') || '[]');
    const entryBids = allBids.filter(b => b.listingId === entry.id);
    if (entryBids.some(b => b.status === 'Accepted')) return 'Accepted';
    if (entryBids.some(b => b.status === 'Countered')) return 'Countered';
    if (entryBids.some(b => b.status === 'Rejected')) return 'Rejected';
    if (isExpired) return 'Expired';
    if (entryBids.length > 0) return 'Bids Received';
    return 'Live';
  };

  const renderDropdown = (label, field, options, customField) => (
    <>
      <div className="input-box">
        <label>{label}</label>
        <select
          value={currentEntry[field]}
          onChange={(e) => handleInputChange(field, e.target.value)}
        >
          <option value="">-- Select --</option>
          {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      </div>
      {currentEntry[field] === 'Others' && (
        <div className="input-box">
          <label>Custom</label>
          <input
            type="text"
            placeholder={`Enter ${label}`}
            value={currentEntry[customField] || ''}
            onChange={(e) => handleInputChange(customField, e.target.value)}
          />
        </div>
      )}
    </>
  );

  const handlePaymentDateChange = (field, value) => {
    if (!value) {
      handleInputChange(field, value);
      return;
    }
    const date = new Date(value);
    const day = date.getDate();
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    if (day !== 15 && day !== lastDay) {
      alert(`Payment Due Date must be the 15th or the end of the month (${lastDay}).`);
      return;
    }
    handleInputChange(field, value);
  };

  const renderOptionButtons = (label, field, options) => (
    <div className="input-box options-group">
      <label>{label}</label>
      <div className="option-button-container">
        {options.map(opt => (
          <button
            key={opt}
            type="button"
            className={`opt-btn ${currentEntry[field] === opt ? 'selected' : ''}`}
            onClick={() => handleInputChange(field, opt)}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );

  // --- Custom Parameter Helpers (for non-Cotton products) ---
  const addCustomParam = () => {
    setCurrentEntry(prev => ({
      ...prev,
      customParams: [...prev.customParams, { title: '', value: '' }]
    }));
  };

  const removeCustomParam = (index) => {
    setCurrentEntry(prev => ({
      ...prev,
      customParams: prev.customParams.filter((_, i) => i !== index)
    }));
  };

  const updateCustomParam = (index, field, value) => {
    setCurrentEntry(prev => ({
      ...prev,
      customParams: prev.customParams.map((p, i) => i === index ? { ...p, [field]: value } : p)
    }));
  };

  // Determine if this is a Cotton product (uses predefined fields)
  const isCottonProduct = currentProduct === 'cotton';

  const renderNumericInput = (label, field) => (
    <div className="input-box">
      <label>{label}</label>
      <input
        type="text"
        placeholder="e.g. 1.2"
        value={currentEntry[field] || ''}
        onChange={(e) => {
          const val = e.target.value.replace(/[^0-9.]/g, '');
          handleInputChange(field, val);
        }}
      />
    </div>
  );

  const renderMinMaxFields = (label, minField, maxField) => (
    <div className="input-box min-max-box" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <label style={{ fontSize: '0.65rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>{label}</label>
      <div className="min-max-inputs" style={{ display: 'flex', gap: '4px' }}>
        <input
          type="text"
          placeholder="Min"
          value={currentEntry[minField] || ''}
          onChange={(e) => handleInputChange(minField, e.target.value)}
          style={{ width: '48px', padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.75rem', height: '26px' }}
        />
        <input
          type="text"
          placeholder="Max"
          value={currentEntry[maxField] || ''}
          onChange={(e) => handleInputChange(maxField, e.target.value)}
          style={{ width: '48px', padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.75rem', height: '26px' }}
        />
      </div>
    </div>
  );

  const renderTechnicalFields = () => {
    if (!isCottonProduct) {
      return (
        <div className="custom-params-section" style={{ width: '100%' }}>
          <p className="custom-params-hint">Define your own technical parameters for this product. Add rows for each specification.</p>
          {currentEntry.customParams.map((param, idx) => (
            <div key={idx} className="custom-param-row">
              <input
                type="text"
                placeholder="Parameter Name (e.g. Denier)"
                value={param.title}
                onChange={e => updateCustomParam(idx, 'title', e.target.value)}
                className="param-title-input"
              />
              <input
                type="text"
                placeholder="Value (e.g. 1.2D)"
                value={param.value}
                onChange={e => updateCustomParam(idx, 'value', e.target.value)}
                className="param-value-input"
              />
              <button type="button" className="btn-remove-param" onClick={() => removeCustomParam(idx)}>✕</button>
            </div>
          ))}
          <button type="button" className="btn-add-param" onClick={addCustomParam}>+ Add Parameter</button>
        </div>
      );
    }

    const predefinedFields = (() => {
      switch (currentSubType) {
        case 'hvi':
          return (
            <>
              {renderNumericInput('SCI', 'sci')}
              {renderNumericInput('Mic', 'mic')}
              {renderNumericInput('SL (mm)', 'sl')}
              {renderNumericInput('UR (%)', 'ur')}
              {renderNumericInput('SF (%)', 'sf')}
              {renderNumericInput('Str (g/tex)', 'str')}
              {renderNumericInput('Elg (%)', 'elg')}
              {renderNumericInput('Rd', 'rd')}
              {renderNumericInput('+b', 'plusB')}
              {renderNumericInput('CGrd', 'cgrd')}
              {renderOptionButtons('Test Report?', 'hasTestReport', ['No', 'Yes'])}
              {currentEntry.hasTestReport === 'Yes' && (
                <div className="input-box upload-group-compact" style={{ width: '92px', minWidth: '92px' }}>
                  <label>Upload Report</label>
                  <label className="file-input-label-compact file-btn-block" style={{ cursor: 'pointer', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '4px', padding: '3px 6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', height: '24px', fontWeight: '600' }}>
                    {currentEntry.testReportFile ? '✓ Attached' : '📥 Upload'}
                    <input type="file" accept=".pdf, .jpeg, .jpg" style={{ display: 'none' }} onChange={e => handleInputChange('testReportFile', e.target.files[0])} />
                  </label>
                </div>
              )}
            </>
          );
        case 'domestic-desc':
          return (
            <>
              {renderMinMaxFields('Mic Range', 'micMin', 'micMax')}
              {renderMinMaxFields('SL (mm)', 'slMin', 'slMax')}
              {renderMinMaxFields('Moisture (%)', 'moistureMin', 'moistureMax')}
              {renderMinMaxFields('Trash (%)', 'trashMin', 'trashMax')}
              {renderMinMaxFields('Str (g/tex)', 'strMin', 'strMax')}
              {renderMinMaxFields('Rd', 'rdMin', 'rdMax')}
            </>
          );
        case 'import-desc':
          return (
            <>
              {renderDropdown('Origin', 'origin', origins, 'custom_origin')}
              {renderDropdown('Growth', 'growth', growths, 'custom_growth')}
              {renderDropdown('Colour Grade', 'colourGrade', ['11-1', '21-1', 'Others'], 'custom_cgrd')}
              {renderMinMaxFields('Leaf', 'leafMin', 'leafMax')}
              {renderMinMaxFields('Staple', 'stapleMin', 'stapleMax')}
              {renderMinMaxFields('Mic', 'micMin', 'micMax')}
              {renderMinMaxFields('GPT', 'gptMin', 'gptMax')}
              {renderMinMaxFields('GPT Min/Avg', 'gptMinAvgMin', 'gptMinAvgMax')}
              <div className="input-box"><label>Ship from</label><input type="text" value={currentEntry.shipFrom} onChange={e => handleInputChange('shipFrom', e.target.value)} /></div>
              <div className="input-box"><label>Shipment</label><input type="text" value={currentEntry.shipment} onChange={e => handleInputChange('shipment', e.target.value)} /></div>
              <div className="input-box"><label>ETA of TSP</label><input type="text" value={currentEntry.eta} onChange={e => handleInputChange('eta', e.target.value)} /></div>
              <div className="input-box"><label>Cover Month</label><input type="text" value={currentEntry.coverMonth} onChange={e => handleInputChange('coverMonth', e.target.value)} /></div>
            </>
          );
        case 'import-recap':
          return (
            <>
              {renderDropdown('Origin', 'origin', origins, 'custom_origin')}
              {renderDropdown('Growth', 'growth', growths, 'custom_growth')}
              <div className="input-box">
                <label>Offering MT</label>
                <input
                  type="number"
                  min="0"
                  placeholder="e.g. 50"
                  value={currentEntry.offerQty}
                  onChange={e => handleInputChange('offerQty', e.target.value)}
                />
              </div>
              {renderDropdown('Colour grade', 'colourGrade', ['11-1', '21-1', 'Others'], 'custom_cgrd')}
              {renderMinMaxFields('Leaf', 'leafMin', 'leafMax')}
              {renderMinMaxFields('Staple', 'stapleMin', 'stapleMax')}
              {renderMinMaxFields('Mic', 'micMin', 'micMax')}
              {renderMinMaxFields('GPT', 'gptMin', 'gptMax')}
              {renderMinMaxFields('GPT min/Avg', 'gptMinAvgMin', 'gptMinAvgMax')}
              <div className="input-box">
                <label>Recap/Ref File Name</label>
                <input
                  type="text"
                  placeholder="Enter Recap/Ref File Name"
                  value={currentEntry.recapFileName}
                  onChange={e => handleInputChange('recapFileName', e.target.value)}
                />
              </div>
              <div className="input-box">
                <label>Recap File Attachment</label>
                <input
                  type="file"
                  onChange={e => {
                    const file = e.target.files[0];
                    handleInputChange('recapFile', file);
                    if (file && !currentEntry.recapFileName) {
                      handleInputChange('recapFileName', file.name);
                    }
                  }}
                />
              </div>
            </>
          );
        case 'waste':
          return (
            <>
              {renderDropdown('Type of Waste', 'wasteType', wasteTypes, 'custom_wasteType')}
              {renderDropdown('SL (mm)', 'sl', ['12', '14', '16', 'Others'], 'custom_sl')}
              {renderDropdown('Moisture (%)', 'moisture', ['8%', '9%', '10%', 'Others'], 'custom_moisture')}
              {renderOptionButtons('Blend with MMF', 'blendWithMMF', ['No', 'Yes'])}
              {renderOptionButtons('Contamination Free', 'contaminationFree', ['No', 'Yes'])}
              {renderOptionButtons('Cloth covered Bales', 'clothCovered', ['No', 'Yes'])}
            </>
          );
        case 'cert':
          return (
            <>
              <div className="cert-config-table" style={{ width: '100%' }}>
                <span className="cert-row-label">Configure Certificates & Premiums (₹):</span>
                <div className="cert-row-items" style={{ marginTop: '10px', marginBottom: '15px' }}>
                  {certificationsList.map(cert => {
                    const selectedCert = currentEntry.selectedCerts.find(c => c.id === cert.id);
                    const isChecked = !!selectedCert;
                    const premiumVal = selectedCert ? (selectedCert.premium === '' ? '' : selectedCert.premium) : cert.premium;

                    return (
                      <div key={cert.id} className={`cert-row-item-col ${isChecked ? 'active' : ''}`}>
                        <label className="cert-checkbox-label">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleCustomCertToggle(cert.id, cert.premium)}
                          />
                          <span className="cert-name-text">{cert.name}</span>
                        </label>
                        {isChecked && (
                          <div className="cert-premium-below">
                            <input
                              type="number"
                              className="cert-premium-input"
                              placeholder="Price"
                              value={premiumVal}
                              style={{ width: '80px', padding: '4px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                              onChange={(e) => handleCustomCertPremiumChange(cert.id, e.target.value)}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="custom-cert-add-form" style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                  <input
                    type="text"
                    placeholder="Custom Cert Name (e.g. OCS)"
                    value={customCertName}
                    onChange={(e) => setCustomCertName(e.target.value)}
                    style={{ padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem' }}
                  />
                  <input
                    type="number"
                    placeholder="Premium (₹)"
                    value={customCertPremium}
                    onChange={(e) => setCustomCertPremium(e.target.value)}
                    style={{ padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem', width: '120px' }}
                  />
                  <button
                    type="button"
                    className="btn-add-item-small"
                    onClick={handleAddCustomCertType}
                    style={{ padding: '8px 12px', background: '#0d47a1', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600' }}
                  >
                    + Add More Items
                  </button>
                </div>
              </div>
            </>
          );
        default:
          return null;
      }
    })();

    return predefinedFields;
  };

  // renderFileUploadFields has been removed since excel upload is at top and test report is inline next to CGrd.

  return (
    <div className="trade-page-wrapper">
      <div className="market-ticker-container">
        <div className="ticker-content">
          <span>📈 ICE Cotton May: 82.45 (+0.12)</span>
          <span>📉 MCX Cotton Apr: 58200 (-150)</span>
          <span>📈 USD/INR: 83.25 (+0.05)</span>
          <span>📊 Global Cotton Index: 92.10</span>
          <span>📈 Zhengzhou Cotton (ZCE): 16200 (+50)</span>
        </div>
      </div>
      <header className="premium-trade-header">
        <div className="header-top">
          <div className="brand-section">
            <h1 className="header-logo" onClick={() => navigate('/role-selection')}>Go<span>Easy</span>Trade</h1>
            <div className="trading-status-badge"><span className="pulse-dot"></span>Market Open</div>
          </div>

          <div className="header-actions-right" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div className="user-profile-identity">
              <div className="role-master-toggle-premium">
                <button className={`toggle-btn-p ${viewMode === 'seller' ? 'active' : ''}`} onClick={() => setViewMode('seller')}>Seller</button>
                <button className={`toggle-btn-p ${viewMode === 'buyer' ? 'active' : ''}`} onClick={() => setViewMode('buyer')}>Buyer</button>
              </div>
            </div>
            <button 
              onClick={handleLogout}
              style={{
                background: 'transparent',
                color: '#f44336',
                border: '1px solid #f44336',
                padding: '6px 12px',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s'
              }}
              onMouseOver={e => { e.currentTarget.style.background = '#f44336'; e.currentTarget.style.color = 'white'; }}
              onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#f44336'; }}
            >
              Sign Out
            </button>
          </div>
        </div>

        <div className="header-product-nav-container-gray">
          <div className="product-button-grid">
            {productOptions.map(p => (
              <button key={p.id} className={`p-option-btn ${currentProduct === p.id ? 'active' : ''}`} onClick={() => handleProductSelect(p.id)}>
                <span className="p-btn-icon">{p.icon}</span><span className="p-btn-name">{p.name}</span>
              </button>
            ))}
          </div>

          <div className="sub-type-nav-bar animate-fade-in">
            {productOptions.find(p => p.id === currentProduct)?.subTypes?.map(sub => (
              <button key={sub.id} className={`sub-type-tab ${currentSubType === sub.id ? 'active' : ''}`} onClick={() => handleSubTypeSelect(sub.id)}>{sub.name}</button>
            ))}
          </div>
        </div>
      </header>

      <main className="trade-content-area">
        {viewMode === 'buyer' ? (
          <BuyerDomesticHvi
            viewMode={viewMode}
            setViewMode={setViewMode}
            currentProduct={currentProduct}
            currentSubType={currentSubType}
          />
        ) : (
          <div className="seller-dashboard-premium">
            {/* Selling Type Tabs */}
            <div className="selling-type-tabs" style={{ display: 'flex', gap: '10px', marginBottom: '8px', background: '#f8fafc', padding: '6px', borderRadius: '12px', border: '1px solid #e2e8f0', width: 'fit-content' }}>
              <button
                type="button"
                onClick={() => {
                  setSellingType('current');
                  setEntryMode(null);
                  setEditingId(null);
                  setCurrentEntry({ ...initialEntryState, sellingType: 'current' });
                }}
                className={`sub-type-tab ${sellingType === 'current' ? 'active' : ''}`}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                ☀️ Spot Offers
              </button>
              {!(currentProduct === 'cotton' && currentSubType === 'cert') && (
                <button
                  type="button"
                  onClick={() => {
                    setSellingType('forward');
                    setEntryMode(null);
                    setEditingId(null);
                    setCurrentEntry({ ...initialEntryState, sellingType: 'forward' });
                  }}
                  className={`sub-type-tab ${sellingType === 'forward' ? 'active' : ''}`}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  🔮 Forward Offers
                </button>
              )}
            </div>

            <section className="entry-form-card mega-form">
              {!entryMode ? (
                <>
                  <h3>List New {currentProduct === 'cotton' && currentSubType === 'hvi' ? 'Cotton HVI' : currentSubType.toUpperCase().replace('-', ' ')} Stock</h3>
                  <div className="entry-mode-selection-container" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '15px', padding: '15px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', margin: '15px 0' }}>
                    <a href="/Marketplace_Template.xlsx" className="btn-link-small" style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#0d47a1', color: 'white', textDecoration: 'none', padding: '8px 16px', borderRadius: '8px', fontWeight: 'bold', fontSize: '0.82rem' }} download>
                      📥 {currentProduct === 'cotton' && currentSubType === 'hvi' ? 'HVI Report-Download' : currentProduct === 'cotton' && currentSubType.includes('desc') ? 'Description Report-Download' : 'Download Excel Template'}
                    </a>
                    
                    <label className="file-input-label-compact" style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#10b981', color: 'white', padding: '8px 16px', borderRadius: '8px', fontWeight: 'bold', fontSize: '0.82rem', cursor: 'pointer', marginBottom: 0 }}>
                      📤 {currentProduct === 'cotton' && currentSubType === 'hvi' ? 'HVI Report-Upload' : currentProduct === 'cotton' && currentSubType.includes('desc') ? 'Description Report-Upload' : 'Upload Excel Sheet'}
                      <input type="file" accept=".xlsx, .xls" style={{ display: 'none' }} onChange={e => {
                        handleExcelUpload(e.target.files[0]);
                        setEntryMode('excel');
                        e.target.value = '';
                      }} />
                    </label>
                    
                    <button type="button" onClick={() => setEntryMode('manual')} style={{ background: '#64748b', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', fontWeight: 'bold', fontSize: '0.82rem', cursor: 'pointer' }}>
                      ✍ {currentProduct === 'cotton' && currentSubType === 'hvi' ? 'HVI Report-Manual entry' : currentProduct === 'cotton' && currentSubType.includes('desc') ? 'Description Report-Manual entry' : 'Manual Entry'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px' }}>
                    <h3 style={{ margin: 0 }}>List New {currentProduct === 'cotton' && currentSubType === 'hvi' ? 'Cotton HVI' : currentSubType.toUpperCase().replace('-', ' ')} Stock</h3>
                    <button type="button" onClick={() => {
                      setEntryMode(null);
                      setEditingId(null);
                      setCurrentEntry({ ...initialEntryState, sellingType: sellingType });
                    }} style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', color: '#475569', borderRadius: '6px', cursor: 'pointer', padding: '4px 10px', fontSize: '0.75rem', fontWeight: '700' }}>
                      Reset / Back
                    </button>
                  </div>

                  <div className="form-sections-horizontal">
                    <div className="horizontal-group-vertical">
                      <h4 className="section-title-vertical">Primary Details</h4>
                      <div className="fields-row-vertical">
                        {renderDropdown('Crop Year', 'cropYear', cropYears, 'custom_cropYear')}
                        {currentSubType.includes('import') ? (
                          renderDropdown('Origin', 'origin', origins, 'custom_origin')
                        ) : (
                          <>
                            {renderDropdown('State', 'state', states, 'custom_state')}
                            {renderDropdown('Station/Zone', 'station', stations, 'custom_station')}
                          </>
                        )}
                        {(currentSubType === 'hvi' || currentSubType === 'domestic' || currentSubType === 'imported' ||
                          currentSubType === 'comber' || currentSubType === 'fs' ||
                          currentSubType === 'warp' || currentSubType === 'weft' ||
                          currentSubType === 'cotton-fabric' || currentSubType === 'blended' ||
                          currentSubType === 'new' || currentSubType === 'used' ||
                          currentSubType === 'sell' || currentSubType === 'conversion' || currentSubType === 'lease') && (
                            <>
                              <div className="input-box"><label>Gin Lot No / Seller Lot No</label><input type="text" placeholder="e.g. 10532/43" value={currentEntry.lotNumber} onChange={e => handleInputChange('lotNumber', e.target.value)} /></div>
                              {renderDropdown('Warehouse Location', 'warehouse', warehouses, 'custom_warehouse')}
                              {renderDropdown('Name of the Lab', 'labName', labs, 'custom_labName')}
                            </>
                          )}
                        {renderOptionButtons('Offer Qty Terms', 'offerQtyTerms', ['Bales', 'MT'])}
                        {currentEntry.offerQtyTerms && (
                          <div className="input-box" style={{ width: '80px' }}>
                            <label>Quantity</label>
                            <input
                              type="number"
                              min="0"
                              step="50"
                              placeholder="Enter Qty"
                              value={currentEntry.offerQty}
                              onChange={e => handleInputChange('offerQty', e.target.value)}
                              style={{ width: '100%' }}
                            />
                          </div>
                        )}
                        <div className="input-box"><label>Seller Name</label><input type="text" placeholder="Enter Seller Name" value={currentEntry.sellerName} onChange={e => handleInputChange('sellerName', e.target.value)} /></div>
                        {(isCottonProduct && ['domestic-desc', 'import-desc', 'import-recap'].includes(currentSubType)) && (
                          <div className="custom-params-section-inline" style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap', borderLeft: '1px dashed #cbd5e1', paddingLeft: '10px' }}>
                            {currentEntry.customParams.map((param, idx) => (
                              <div key={idx} className="custom-param-row" style={{ display: 'flex', gap: '4px', alignItems: 'center', background: '#f8fafc', padding: '4px 8px', borderRadius: '6px', border: '1px solid #e2e8f0', marginBottom: 0 }}>
                                <div className="input-box" style={{ width: '85px' }}>
                                  <label style={{ fontSize: '0.6rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' }}>Name</label>
                                  <input
                                    type="text"
                                    placeholder="Name"
                                    value={param.title}
                                    onChange={e => updateCustomParam(idx, 'title', e.target.value)}
                                    style={{ padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.75rem', height: '24px', width: '100%' }}
                                  />
                                </div>
                                <div className="input-box" style={{ width: '75px' }}>
                                  <label style={{ fontSize: '0.6rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' }}>Value</label>
                                  <input
                                    type="text"
                                    placeholder="Value"
                                    value={param.value}
                                    onChange={e => updateCustomParam(idx, 'value', e.target.value)}
                                    style={{ padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.75rem', height: '24px', width: '100%' }}
                                  />
                                </div>
                                <button type="button" className="btn-remove-param" onClick={() => removeCustomParam(idx)} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0 4px', fontSize: '0.9rem', fontWeight: 'bold', alignSelf: 'flex-end', marginBottom: '4px' }}>✕</button>
                              </div>
                            ))}
                            <button type="button" className="btn-add-param" onClick={addCustomParam} style={{ background: '#f0f7ff', border: '1px solid #bfdbfe', color: '#0d47a1', borderRadius: '4px', cursor: 'pointer', padding: '4px 8px', fontSize: '0.75rem', fontWeight: '700', height: '26px', alignSelf: 'flex-end' }}>+ Add Param</button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="horizontal-group-vertical">
                      <h4 className="section-title-vertical">Technical Details</h4>
                      <div className="fields-row-vertical technical-parameters">
                        {renderTechnicalFields()}
                      </div>
                    </div>

                    <div className="horizontal-group-vertical">
                      <h4 className="section-title-vertical">Pricing & Trade Terms (INCOTERMS)</h4>
                      <div className="fields-row-vertical pricing-fields-wrap">
                        <div className="fields-row compact-fields-row" style={{ flexWrap: 'wrap', gap: '10px', width: '100%' }}>
                          {sellingType === 'forward' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <label style={{ fontSize: '0.58rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1px', display: 'block' }}>Forward Offers</label>
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setForwardOffersTemp(currentEntry.forwardOffers?.length ? [...currentEntry.forwardOffers] : [{ id: Date.now(), paymentDueDate: '', coverMonth: 'K26', spotBasis: '', fwdBasisPrem: '', forexPrem: '', offerPrice: '' }]);
                                    setShowForwardModal(true);
                                  }}
                                  style={{ background: '#1a3060', color: 'white', border: 'none', borderRadius: '4px', padding: '3px 10px', cursor: 'pointer', fontSize: '0.7rem', fontWeight: '700', height: '24px', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '5px' }}
                                >
                                  📅 Setup Offers
                                </button>
                                {currentEntry.forwardOffers && currentEntry.forwardOffers.length > 0 && (
                                  <div style={{ fontSize: '0.7rem', color: '#0f172a', fontWeight: '600', display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                                    {currentEntry.forwardOffers.map(o => (
                                      <span key={o.id} style={{ background: '#e2e8f0', padding: '2px 6px', borderRadius: '4px', border: '1px solid #cbd5e1' }}>
                                        {o.paymentDueDate ? new Date(o.paymentDueDate).toLocaleDateString('en-GB', {day:'2-digit', month:'short'}) : 'No Date'} : ₹{parseInt(o.offerPrice || 0).toLocaleString()}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {isCottonProduct ? (
                            <>
                              {!isCertOrWaste && renderOptionButtons('Pricing Type', 'pricingType', ['Flat', 'Basis'])}
                              {currentEntry.pricingType === 'Basis' ? (
                                <>
                                  <div className="input-box" style={{ width: '140px' }}>
                                    <label>Cover Month (ICE: {cottonRates[currentEntry.coverMonth] || 0})</label>
                                    <select
                                      value={currentEntry.coverMonth || 'K26'}
                                      onChange={(e) => handleInputChange('coverMonth', e.target.value)}
                                      style={{ width: '100%' }}
                                    >
                                      {(() => {
                                        const codeToName = { K: 'May', N: 'Jul', V: 'Oct', Z: 'Dec', H: 'Mar' };
                                        return Object.keys(cottonRates)
                                          .filter(k => /^[KNVZH]\d{2}$/.test(k))
                                          .sort((a, b) => {
                                            const yA = parseInt(a.slice(1)), yB = parseInt(b.slice(1));
                                            return yA !== yB ? yA - yB : 'KNVZH'.indexOf(a[0]) - 'KNVZH'.indexOf(b[0]);
                                          })
                                          .map(key => (
                                            <option key={key} value={key}>
                                              {`${codeToName[key[0]]}-${key.slice(1)} (${key})`}
                                            </option>
                                          ));
                                      })()}
                                    </select>
                                  </div>
                                  <div className="input-box" style={{ width: '75px' }}>
                                    <label>Basis (PTS)</label>
                                    <input
                                      type="number"
                                      placeholder="e.g. 1500"
                                      value={currentEntry.basisPoints}
                                      onChange={(e) => handleInputChange('basisPoints', e.target.value)}
                                      style={{ width: '100%' }}
                                    />
                                  </div>
                                  <div className="input-box" style={{ width: '100px' }}>
                                    <label>Fx Prem (Spot: {cottonRates.USDINR || 83.25})</label>
                                    <input
                                      type="number"
                                      placeholder="e.g. 0"
                                      value={currentEntry.forexPrem}
                                      onChange={(e) => handleInputChange('forexPrem', e.target.value)}
                                      style={{ width: '100%' }}
                                    />
                                  </div>
                                  <div className="input-box" style={{ width: '90px' }}>
                                    <label>Offer Price</label>
                                    <input
                                      type="text"
                                      value={`₹${parseInt(currentEntry.offerPrice || 0).toLocaleString()}`}
                                      readOnly
                                      style={{ background: '#f1f5f9', fontWeight: 'bold', color: '#0d47a1', width: '100%' }}
                                    />
                                  </div>
                                </>
                              ) : (
                                <div className="input-box" style={{ width: '100px' }}>
                                  <label>Offer Price (₹)</label>
                                  <input
                                    type="number"
                                    step="100"
                                    placeholder="e.g. 58000"
                                    value={currentEntry.offerPrice}
                                    onChange={(e) => handleInputChange('offerPrice', e.target.value)}
                                    style={{ width: '100%', fontWeight: 'bold', color: '#0d47a1' }}
                                  />
                                </div>
                              )}
                            </>
                          ) : (
                            currentSubType.includes('import') ? (
                              <>
                                <div className="input-box" style={{ width: '100px' }}><label>CIF India Basis</label><input type="text" value={currentEntry.cifIndiaBasis} onChange={e => handleInputChange('cifIndiaBasis', e.target.value)} style={{ width: '100%' }} /></div>
                                <div className="input-box" style={{ width: '80px' }}><label>USC/LBS</label><input type="text" value={currentEntry.uscLbs} onChange={e => handleInputChange('uscLbs', e.target.value)} style={{ width: '100%' }} /></div>
                                <div className="input-box" style={{ width: '90px' }}><label>INR/Candy</label><input type="text" value={currentEntry.inrCandy} onChange={e => handleInputChange('inrCandy', e.target.value)} style={{ width: '100%' }} /></div>
                              </>
                            ) : (
                              renderDropdown('Offer Price (₹)', 'offerPrice', ['55000', '58000', '60000', 'Others'], 'custom_offerPrice')
                            )
                          )}

                          {sellingType !== 'forward' && (
                            <div className="input-box" style={{ width: '130px' }}>
                              <label>Due Date</label>
                              <input type="date" value={currentEntry.validDate || ''} onChange={e => handlePaymentDateChange('validDate', e.target.value)} style={{ width: '100%' }} />
                            </div>
                          )}

                          {currentSubType !== 'cert' && renderOptionButtons('Certification?', 'certification', ['No', 'Yes'])}
                          {currentSubType !== 'cert' && currentEntry.certification === 'Yes' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <label style={{ fontSize: '0.58rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1px', display: 'block' }}>Setup</label>
                              <button
                                type="button"
                                onClick={() => setShowCertModal(true)}
                                style={{ background: '#1a3060', color: 'white', border: 'none', borderRadius: '4px', padding: '3px 10px', cursor: 'pointer', fontSize: '0.7rem', fontWeight: '700', height: '24px', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '5px' }}
                              >
                                🏷️ {currentEntry.selectedCerts.length > 0 ? `${currentEntry.selectedCerts.length} Cert${currentEntry.selectedCerts.length > 1 ? 's' : ''} · ₹${calculateTotalPremium().toLocaleString()}` : 'Configure Certs'}
                              </button>
                            </div>
                          )}

                          {renderOptionButtons('IncoTerms', 'transportService', ['EX.Whs', 'FOR'])}
                          {currentEntry.transportService === 'FOR' && (
                            <div className="input-box" style={{ width: '120px' }}>
                              <label>State</label>
                              <input
                                type="text"
                                placeholder="Enter State"
                                value={currentEntry.forState || ''}
                                onChange={(e) => handleInputChange('forState', e.target.value)}
                                style={{ width: '100%' }}
                              />
                            </div>
                          )}
                          <div className="input-box" style={{ width: '120px' }}>
                            <label>Remarks</label>
                            <input
                              type="text"
                              placeholder="e.g. Price negotiable"
                              value={currentEntry.remarks}
                              onChange={e => handleInputChange('remarks', e.target.value)}
                              style={{ width: '100%' }}
                            />
                          </div>
                        </div>

                        {showCertModal && (
                          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div style={{ background: 'white', borderRadius: '14px', padding: '24px', width: '360px', maxHeight: '82vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px' }}>
                                <h4 style={{ margin: 0, color: '#0d47a1', fontSize: '1rem', fontWeight: '700' }}>🏷️ Certification Offer Prices</h4>
                                <button type="button" onClick={() => setShowCertModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#64748b', lineHeight: 1 }}>×</button>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px', gap: '6px 10px', alignItems: 'center', marginBottom: '6px', padding: '0 4px' }}>
                                <span style={{ fontWeight: '700', fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Certificate</span>
                                <span style={{ fontWeight: '700', fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Offer Price (₹)</span>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                {certificationsList.map(cert => {
                                  const selectedCert = currentEntry.selectedCerts.find(c => c.id === cert.id);
                                  const isChecked = !!selectedCert;
                                  const premiumVal = selectedCert ? (selectedCert.premium === '' ? '' : selectedCert.premium) : cert.premium;
                                  return (
                                    <div key={cert.id} style={{ display: 'grid', gridTemplateColumns: '1fr 110px', gap: '6px 10px', alignItems: 'center', padding: '7px 10px', borderRadius: '7px', background: isChecked ? '#eff6ff' : '#f8fafc', border: `1px solid ${isChecked ? '#bfdbfe' : '#e2e8f0'}`, transition: 'all 0.15s' }}>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: isChecked ? '700' : '500', color: isChecked ? '#1e40af' : '#475569', margin: 0 }}>
                                        <input
                                          type="checkbox"
                                          checked={isChecked}
                                          onChange={() => handleCustomCertToggle(cert.id, cert.premium)}
                                          style={{ accentColor: '#0d47a1', width: '15px', height: '15px' }}
                                        />
                                        {cert.name}
                                      </label>
                                      <input
                                        type="number"
                                        step="100"
                                        placeholder="0"
                                        value={premiumVal}
                                        disabled={!isChecked}
                                        onChange={(e) => handleCustomCertPremiumChange(cert.id, e.target.value)}
                                        style={{ width: '100%', padding: '5px 8px', border: `1px solid ${isChecked ? '#93c5fd' : '#e2e8f0'}`, borderRadius: '5px', fontSize: '0.85rem', background: isChecked ? 'white' : '#f1f5f9', color: isChecked ? '#1e40af' : '#94a3b8', fontWeight: isChecked ? '700' : '400', boxSizing: 'border-box' }}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                              <div style={{ marginTop: '14px', padding: '10px 14px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '0.85rem', color: '#0369a1', fontWeight: '600' }}>Total Premium</span>
                                <strong style={{ color: '#0d47a1', fontSize: '1rem' }}>₹{calculateTotalPremium().toLocaleString()}</strong>
                              </div>
                              <button
                                type="button"
                                onClick={() => setShowCertModal(false)}
                                style={{ marginTop: '14px', width: '100%', background: 'linear-gradient(135deg, #0d47a1, #1565c0)', color: 'white', border: 'none', borderRadius: '8px', padding: '11px', cursor: 'pointer', fontWeight: '700', fontSize: '0.92rem' }}
                              >
                                ✓ Done
                              </button>
                            </div>
                          </div>
                        )}

                        {showForwardModal && (
                          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                            <div style={{ background: 'white', borderRadius: '12px', width: '90%', maxWidth: '800px', padding: '20px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px' }}>
                                <h4 style={{ margin: 0, color: '#0d47a1', fontSize: '1rem', fontWeight: '700' }}>📅 Setup Forward Offers ({currentEntry.pricingType})</h4>
                                <button type="button" onClick={() => setShowForwardModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#64748b', lineHeight: 1 }}>×</button>
                              </div>
                              <div style={{ overflowY: 'auto', flex: 1, padding: '0 4px', paddingRight: '8px' }}>
                                {forwardOffersTemp.map((offer, index) => (
                                  <div key={offer.id} style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px', background: '#f8fafc', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
                                    <button type="button" onClick={() => setForwardOffersTemp(prev => prev.filter((_, i) => i !== index))} style={{ position: 'absolute', right: '-8px', top: '-8px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                                    
                                    <div className="input-box" style={{ width: '130px' }}><label>Pymt Due Date</label><input type="date" value={offer.paymentDueDate} onChange={e => { const updated = [...forwardOffersTemp]; updated[index].paymentDueDate = e.target.value; setForwardOffersTemp(updated); }} style={{ width: '100%' }} /></div>
                                    
                                    {currentEntry.pricingType === 'Basis' ? (
                                      <>
                                        <div className="input-box" style={{ width: '140px' }}><label>Cover Month (ICE: {cottonRates[offer.coverMonth || 'K26'] || 0})</label><select value={offer.coverMonth || 'K26'} onChange={e => { const updated = [...forwardOffersTemp]; updated[index].coverMonth = e.target.value; setForwardOffersTemp(updated); }} style={{ width: '100%' }}>{Object.keys(cottonRates).filter(k => /^[KNVZH]\d{2}$/.test(k)).sort((a,b)=>a.slice(1)-b.slice(1) || 'KNVZH'.indexOf(a[0])-'KNVZH'.indexOf(b[0])).map(k => <option key={k} value={k}>{k}</option>)}</select></div>
                                        <div className="input-box" style={{ width: '80px' }}><label>Spot Basis</label><input type="number" placeholder="e.g. 1000" value={offer.spotBasis} onChange={e => { const updated = [...forwardOffersTemp]; updated[index].spotBasis = e.target.value; setForwardOffersTemp(updated); }} style={{ width: '100%' }} /></div>
                                        <div className="input-box" style={{ width: '95px' }}><label>Fwd Basis Prem</label><input type="number" placeholder="e.g. 500" value={offer.fwdBasisPrem} onChange={e => { const updated = [...forwardOffersTemp]; updated[index].fwdBasisPrem = e.target.value; setForwardOffersTemp(updated); }} style={{ width: '100%' }} /></div>
                                        <div className="input-box" style={{ width: '100px' }}><label>Fx Prem (Spot: {cottonRates.USDINR || 83.25})</label><input type="number" placeholder="e.g. 10" value={offer.forexPrem} onChange={e => { const updated = [...forwardOffersTemp]; updated[index].forexPrem = e.target.value; setForwardOffersTemp(updated); }} style={{ width: '100%' }} /></div>
                                        <div className="input-box" style={{ width: '100px' }}><label>Offer Price</label><input type="text" value={(() => { const ice = parseFloat(cottonRates[offer.coverMonth || 'K26'] || 0); const basis = (parseFloat(offer.spotBasis || 0) + parseFloat(offer.fwdBasisPrem || 0)) / 100; const fx = parseFloat(cottonRates.USDINR || 83.25); const fxPrem = parseFloat(offer.forexPrem || 0) / 100; return '₹' + Math.round((ice + basis) * 7.84 * (fx + fxPrem)).toLocaleString(); })()} readOnly style={{ width: '100%', background: '#f1f5f9', fontWeight: 'bold' }} /></div>
                                      </>
                                    ) : (
                                      <div className="input-box" style={{ width: '120px' }}><label>Offer Price (₹)</label><input type="number" value={offer.offerPrice} onChange={e => { const updated = [...forwardOffersTemp]; updated[index].offerPrice = e.target.value; setForwardOffersTemp(updated); }} style={{ width: '100%' }} /></div>
                                    )}
                                  </div>
                                ))}
                                <button type="button" onClick={() => setForwardOffersTemp(prev => [...prev, { id: Date.now(), paymentDueDate: '', coverMonth: 'K26', spotBasis: '', fwdBasisPrem: '', forexPrem: '', offerPrice: '' }])} style={{ background: '#f0f7ff', border: '1px dashed #bfdbfe', color: '#0d47a1', padding: '8px', borderRadius: '6px', cursor: 'pointer', width: '100%', fontWeight: 'bold', fontSize: '0.85rem' }}>+ Add Row</button>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  setCurrentEntry(prev => ({
                                    ...prev,
                                    forwardOffers: forwardOffersTemp,
                                    ...(forwardOffersTemp.length > 0 ? {
                                      paymentDueDate: forwardOffersTemp[0].paymentDueDate,
                                      offerPrice: currentEntry.pricingType === 'Basis' 
                                        ? String(Math.round((parseFloat(cottonRates[forwardOffersTemp[0].coverMonth || 'K26'] || 0) + ((parseFloat(forwardOffersTemp[0].spotBasis || 0) + parseFloat(forwardOffersTemp[0].fwdBasisPrem || 0)) / 100)) * 7.84 * (parseFloat(cottonRates.USDINR || 83.25) + (parseFloat(forwardOffersTemp[0].forexPrem || 0) / 100))))
                                        : forwardOffersTemp[0].offerPrice
                                    } : {})
                                  }));
                                  setShowForwardModal(false);
                                }}
                                style={{ marginTop: '14px', width: '100%', background: 'linear-gradient(135deg, #0d47a1, #1565c0)', color: 'white', border: 'none', borderRadius: '8px', padding: '11px', cursor: 'pointer', fontWeight: '700', fontSize: '0.92rem' }}
                              >
                                ✓ Save Forward Offers
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="form-footer-security compact-footer">
                    <div className="tc-check">
                      <input type="checkbox" checked={currentEntry.termsAccepted} onChange={e => handleInputChange('termsAccepted', e.target.checked)} id="tc-box" />
                      <label htmlFor="tc-box" style={{ fontSize: '1rem', fontWeight: 'bold' }}>I accept the <span style={{ color: '#0d47a1', cursor: 'pointer', textDecoration: 'underline' }} onClick={(e) => { e.preventDefault(); setShowTerms(true); }}>Terms & Conditions</span></label>
                    </div>
                    <div className="footer-actions-wrap">
                      <div className="pin-security">
                        <label>Security PIN:</label>
                        <input type="password" maxLength="4" placeholder="****" value={currentEntry.securityPin} onChange={e => handleInputChange('securityPin', e.target.value)} />
                      </div>
                      {editingId ? (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button className="btn-save-listing-large compact-btn" style={{ background: '#f59e0b' }} onClick={saveEntry}>UPDATE LOT DETAILS</button>
                          <button className="btn-save-listing-large compact-btn" style={{ background: '#64748b' }} onClick={() => {
                            setEditingId(null);
                            setCurrentEntry(initialEntryState);
                            setEntryMode(null);
                          }}>CANCEL</button>
                        </div>
                      ) : (
                        <button className="btn-save-listing-large compact-btn" onClick={saveEntry}>PUBLISH FOR SALE</button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </section>

            {managedListingId && (
              <div className="modal-overlay-blur">
                <div className="modal-card-premium bids-modal">
                  <div className="card-header-flex">
                    <h3>
                      <i className="terminal-icon">📟</i> Bids Response Terminal
                      <span className="terminal-lot-badge" style={{ marginLeft: '15px', padding: '4px 8px', background: '#f1f5f9', borderRadius: '6px', fontSize: '0.85rem', color: '#0d47a1', border: '1px solid #e2e8f0', fontWeight: '700' }}>
                        Lot/Bale No: {savedEntries.find(e => e.id === managedListingId)?.lotNumber || 'N/A'}
                      </span>
                    </h3>
                    <button className="btn-close-bids close-x" onClick={() => setManagedListingId(null)}>×</button>
                  </div>

                  <div className="bid-table-container">
                    <table className="bid-management-table">
                      <thead>
                        <tr>
                          <th>Buyer</th>
                          <th>Certification</th>
                          <th>Premium</th>
                          <th>Quantity</th>
                          <th>Bid Price</th>
                          <th>Time Left</th>
                          <th>Response</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeBids.length > 0 ? activeBids.map(bid => (
                          <React.Fragment key={bid.id}>
                            <tr className={`status-${bid?.status?.toLowerCase() || 'pending'}`}>
                              <td className="buyer-cell">{bid.buyerName}</td>
                              <td>
                                {bid.includedCerts && bid.includedCerts.length > 0 ? (
                                  <div className="cert-bid-list-small">
                                    {bid.includedCerts.map(c => (
                                      <span key={c.id} className="cert-pill-small active">
                                        {c.name}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="cert-pill-small">None</span>
                                )}
                              </td>
                              <td>
                                {bid.includedCerts && bid.includedCerts.length > 0 ? (
                                  <div className="cert-premium-list-small">
                                    {bid.includedCerts.map(c => (
                                      <div key={c.id} className="cert-premium-item-small">
                                        {c.name}: ₹{c.buyerBidPremium}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  '₹0'
                                )}
                              </td>
                              <td>{bid.quantity} Bales</td>
                              <td className="bid-price-val">
                                ₹{parseFloat(bid.bidPrice || 0).toLocaleString()} 
                                {bid.bidDiffSelected && bid.bidDiffSelected !== 'other' && (
                                  <span className="diff-tag"> ({parseFloat(bid.bidDiffSelected) > 0 ? '+' : ''}{bid.bidDiffSelected})</span>
                                )}
                              </td>
                              <td>
                                <span className={`time-tag-exp ${calculateTimeLeft(savedEntries.find(e => e.id === bid.listingId)?.validDate, savedEntries.find(e => e.id === bid.listingId)?.closingTime) === 'EXPIRED' ? 'expired' : ''}`}>
                                  {calculateTimeLeft(savedEntries.find(e => e.id === bid.listingId)?.validDate, savedEntries.find(e => e.id === bid.listingId)?.closingTime)}
                                </span>
                              </td>
                              <td>
                                {bid.status === 'Countering' ? (
                                  <div className="inline-counter-controls animate-scale-up">
                                    <div className="inline-counter-input-item">
                                      <span className="inline-counter-label">Price:</span>
                                      <input
                                        type="number"
                                        placeholder="Price"
                                        value={bid.counterProductPrice !== undefined ? bid.counterProductPrice : bid.bidPrice}
                                        onChange={e => handleCounterPriceChange(bid.id, 'counterProductPrice', e.target.value)}
                                        className="inline-counter-input"
                                      />
                                    </div>
                                    {bid.includedCerts && bid.includedCerts.length > 0 && (
                                      <div className="inline-counter-certs-wrap">
                                        {bid.includedCerts.map(c => {
                                          const currentCounterVal = bid.counterCertPrices?.[c.id] !== undefined ? bid.counterCertPrices[c.id] : c.buyerBidPremium;
                                          return (
                                            <div key={c.id} className="inline-counter-cert-item">
                                              <span className="inline-counter-cert-name">{c.name}:</span>
                                              <input
                                                type="number"
                                                placeholder="Prem"
                                                value={currentCounterVal}
                                                onChange={e => handleCounterCertPriceChange(bid.id, c.id, e.target.value)}
                                                className="inline-counter-cert-input"
                                              />
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                    <button
                                      className="btn-inline-submit-counter"
                                      title="Verify PIN & Send Counter Offer"
                                      onClick={() => {
                                        const prodVal = parseFloat(bid.counterProductPrice !== undefined ? bid.counterProductPrice : bid.bidPrice);
                                        if (prodVal % 100 !== 0) {
                                          alert('Counter product price must be a multiple of 100.');
                                          return;
                                        }
                                        
                                        const finalCertPrices = {};
                                        if (bid.includedCerts) {
                                          for (const c of bid.includedCerts) {
                                            const val = parseFloat(bid.counterCertPrices?.[c.id] !== undefined ? bid.counterCertPrices[c.id] : c.buyerBidPremium);
                                            if (val % 100 !== 0) {
                                              alert(`Counter premium for ${c.name} must be a multiple of 100.`);
                                              return;
                                            }
                                            finalCertPrices[c.id] = val;
                                          }
                                        }

                                        setPinActionData({
                                          bidId: bid.id,
                                          action: 'Countered',
                                          additionalData: {
                                            counterProductPrice: prodVal,
                                            counterCertPrices: finalCertPrices
                                          }
                                        });
                                      }}
                                    >
                                      ✓
                                    </button>
                                    <button 
                                      className="btn-inline-cancel-counter" 
                                      title="Cancel"
                                      onClick={() => handleBidAction(bid.id, 'Pending')}
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ) : bid.status === 'Countered' ? (
                                  <div className="sent-counter-details" style={{ fontSize: '0.74rem', color: '#475569', lineHeight: '1.4' }}>
                                    <div>Counter Price: <strong>₹{parseFloat(bid.counterProductPrice || bid.bidPrice).toLocaleString()}</strong></div>
                                    {bid.counterCertPrices && Object.keys(bid.counterCertPrices).map(cId => {
                                      const val = bid.counterCertPrices[cId];
                                      const cert = (bid.includedCerts || []).find(c => c.id === cId);
                                      return val !== undefined && val !== '' ? (
                                        <div key={cId} style={{ fontSize: '0.68rem', paddingLeft: '5px' }}>
                                          - {cert ? cert.name : cId}: <strong>₹{parseFloat(val).toLocaleString()}</strong>
                                        </div>
                                      ) : null;
                                    })}
                                  </div>
                                ) : (
                                  <div className="action-button-group">
                                    <button className={`btn-act-v2 accept ${bid.status === 'Accepted' ? 'active' : ''}`} onClick={() => handleBidAction(bid.id, 'Accepted')}>Accept</button>
                                    <button className={`btn-act-v2 reject ${bid.status === 'Rejected' ? 'active' : ''}`} onClick={() => handleBidAction(bid.id, 'Rejected')}>Reject</button>
                                    <button className={`btn-act-v2 counter`} onClick={() => handleBidAction(bid.id, 'Countering')}>Counter</button>
                                  </div>
                                )}
                              </td>
                              <td>
                                <span className={`bid-status-pill ${bid.status?.toLowerCase() || 'pending'}`} style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '0.72rem',
                                  fontWeight: '700',
                                  textTransform: 'uppercase',
                                  display: 'inline-block',
                                  backgroundColor: bid.status === 'Accepted' ? '#d1fae5' : (bid.status === 'Rejected' ? '#fee2e2' : (bid.status === 'Countered' ? '#fef3c7' : '#f3f4f6')),
                                  color: bid.status === 'Accepted' ? '#065f46' : (bid.status === 'Rejected' ? '#991b1b' : (bid.status === 'Countered' ? '#92400e' : '#374151')),
                                  border: `1px solid ${bid.status === 'Accepted' ? '#10b981' : (bid.status === 'Rejected' ? '#f87171' : (bid.status === 'Countered' ? '#fbbf24' : '#d1d5db'))}`
                                }}>
                                  {bid.status || 'Pending'}
                                </span>
                              </td>
                            </tr>
                          </React.Fragment>
                        )) : (
                          <tr className="no-bids-row">
                            <td colSpan="8">
                              <div className="empty-bid-state">
                                <span className="empty-icon">⏳</span>
                                <p>No active bids recorded for this lot yet.</p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {pinActionData && (
              <div className="modal-overlay-blur pin-prompt-overlay" style={{ zIndex: 9999 }}>
                <div className="modal-card-premium pin-prompt-card">
                  <div className="card-header-flex">
                    <h4>Confirm Action with PIN</h4>
                    <button className="close-x" onClick={() => setPinActionData(null)}>×</button>
                  </div>
                  <div className="modal-p-body">
                    <p>Enter your 4-digit Security PIN to confirm: <strong>{pinActionData.action}</strong>.</p>
                    <div className="form-p-group" style={{ marginTop: '15px' }}>
                      <input
                        type="password"
                        placeholder="••••"
                        maxLength="4"
                        pattern="\d{4}"
                        inputMode="numeric"
                        id="seller-action-pin"
                        style={{ textAlign: 'center', fontSize: '1.5rem', letterSpacing: '10px', width: '100%' }}
                      />
                    </div>
                  </div>
                  <div className="modal-p-footer" style={{ padding: '15px 25px', display: 'flex', gap: '15px' }}>
                    <button className="btn-cancel" onClick={() => setPinActionData(null)} style={{ flex: 1 }}>Cancel</button>
                    <button className="btn-submit-premium" onClick={() => confirmPinAction(document.getElementById('seller-action-pin')?.value)} style={{ flex: 1 }}>Confirm</button>
                  </div>
                </div>
              </div>
            )}

            <section className="active-listings-card">
              <h3>Lots Published for Sales by GET</h3>
              <div className="table-scroll-container">
                <table className="inventory-table-detailed">
                  <thead>
                    <tr>
                      <th className="col-primary-info">#</th>
                      <th className="col-primary-info">Crop Year</th>
                      <th className="col-primary-info">State / Station</th>
                      <th className="col-primary-info">Lot / Bale No</th>
                      {currentSubType === 'import-recap' ? (
                        <>
                          <th className="col-technical-param">Growth</th>
                          <th className="col-technical-param">Offering MT</th>
                          <th className="col-technical-param">Quality (CG/Leaf/Staple/Mic/GPT)</th>
                          <th className="col-technical-param">Recap/Ref File</th>
                        </>
                      ) : !isCottonProduct ? (
                        <>
                          {savedEntries.filter(e => e.productType === currentProduct && e.subType === currentSubType)
                            .flatMap(e => e.customParams || [])
                            .filter((v, i, a) => a.findIndex(t => t.title === v.title) === i)
                            .slice(0, 3)
                            .map(p => <th className="col-technical-param" key={p.title}>{p.title}</th>)
                          }
                        </>
                      ) : (
                        <th className="col-technical-param">Quality Preview<br/><span className="th-subtext">(Staple, Mic, Str, Rd, +b)</span></th>
                      )}
                      <th className="col-pricing">Time Left</th>
                      <th className="col-pricing">Price / Premium</th>
                      <th className="col-pricing">Status</th>
                      <th className="col-pricing">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {savedEntries.filter(e => e.subType === currentSubType && (e.productType === currentProduct || !e.productType)).map((entry, idx) => (
                      <tr key={entry.id}>
                        <td className="col-primary-info">{idx + 1}</td>
                        <td className="col-primary-info">{entry.cropYear}</td>
                        <td className="col-primary-info multi-line">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <span>{entry.state || entry.origin}</span>
                            {entry.sellingType === 'forward' && (
                              <span style={{ fontSize: '0.65rem', background: '#e0f2fe', color: '#0369a1', padding: '1px 4px', borderRadius: '4px', fontWeight: 'bold' }}>Forward</span>
                            )}
                          </div>
                          <span className="sub-text">{entry.station || entry.subType}</span>
                          {entry.sellingType === 'forward' && entry.startDate && entry.endDate && (
                            <span style={{ fontSize: '0.65rem', color: '#0d47a1', fontWeight: 'bold', display: 'block', marginTop: '2px' }}>
                              Del: {entry.startDate} to {entry.endDate}
                            </span>
                          )}
                        </td>
                        <td className="col-primary-info">{entry.lotNumber || '-'}</td>
                        {currentSubType === 'import-recap' ? (
                          <>
                            <td className="col-technical-param">{entry.growth || '-'}</td>
                            <td className="col-technical-param">{entry.offerQty ? `${entry.offerQty} MT` : '-'}</td>
                            <td className="col-technical-param quality-preview-compact">
                              {[
                                entry.colourGrade ? `CG:${entry.colourGrade}` : '',
                                (entry.leafMin || entry.leafMax) ? `Leaf:${entry.leafMin || '-'}-${entry.leafMax || '-'}` : '',
                                (entry.stapleMin || entry.stapleMax) ? `Staple:${entry.stapleMin || '-'}-${entry.stapleMax || '-'}` : '',
                                (entry.micMin || entry.micMax) ? `Mic:${entry.micMin || '-'}-${entry.micMax || '-'}` : '',
                                (entry.gptMin || entry.gptMax) ? `GPT:${entry.gptMin || '-'}-${entry.gptMax || '-'}` : '',
                                (entry.gptMinAvgMin || entry.gptMinAvgMax) ? `GPT Avg:${entry.gptMinAvgMin || '-'}-${entry.gptMinAvgMax || '-'}` : ''
                              ].filter(Boolean).join(' | ') || '-'}
                            </td>
                            <td className="col-technical-param">
                              {entry.recapFileName ? `📄 ${entry.recapFileName}` : '-'}
                            </td>
                          </>
                        ) : !isCottonProduct ? (
                          <>
                            {savedEntries.filter(e => e.productType === currentProduct && e.subType === currentSubType)
                              .flatMap(e => e.customParams || [])
                              .filter((v, i, a) => a.findIndex(t => t.title === v.title) === i)
                              .slice(0, 3)
                              .map(p => {
                                const found = (entry.customParams || []).find(cp => cp.title === p.title);
                                return <td className="col-technical-param" key={p.title}>{found ? found.value : '-'}</td>;
                              })
                            }
                          </>
                        ) : (
                          <td className="col-technical-param quality-preview-compact">
                            {entry.sl ? `${entry.sl}mm` : '-'}/
                            {entry.mic || entry.micRange ? `${entry.mic || entry.micRange}mic` : '-'}/
                            {entry.str ? `${entry.str}Gtex` : '-'}/
                            {entry.rd ? `${entry.rd}Rd` : '-'}/
                            {entry.plusB ? `${entry.plusB}+b` : '-'}
                          </td>
                        )}
                        <td className="col-pricing">
                          {(() => {
                            const dateToCheck = entry.sellingType === 'forward' ? entry.endDate : entry.validDate;
                            const tl = calculateTimeLeft(dateToCheck, entry.closingTime);
                            return <span className={`time-tag-mini ${tl === 'EXPIRED' ? 'expired' : ''}`}>{tl}</span>;
                          })()}
                        </td>
                        <td className="col-pricing price-cell"><span className="main-price">₹{parseInt(entry.offerPrice || 0).toLocaleString()} / ₹{entry.totalPremium || 0}</span></td>
                        <td className="col-pricing">
                          {(() => {
                            const st = getListingStatus(entry);
                            const bg = st === 'Accepted' ? '#d1fae5' : (st === 'Rejected' ? '#fee2e2' : (st === 'Countered' ? '#fef3c7' : (st === 'Expired' ? '#fde8e8' : (st === 'Bids Received' ? '#e0e7ff' : '#f3f4f6'))));
                            const fg = st === 'Accepted' ? '#065f46' : (st === 'Rejected' ? '#991b1b' : (st === 'Countered' ? '#92400e' : (st === 'Expired' ? '#991b1b' : (st === 'Bids Received' ? '#3730a3' : '#374151'))));
                            const bd = st === 'Accepted' ? '#10b981' : (st === 'Rejected' ? '#f87171' : (st === 'Countered' ? '#fbbf24' : (st === 'Expired' ? '#f87171' : (st === 'Bids Received' ? '#818cf8' : '#d1d5db'))));
                            return <span style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', background: bg, color: fg, border: `1px solid ${bd}` }}>{st}</span>;
                          })()}
                        </td>
                        <td className="col-pricing">
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center', justifyContent: 'center' }}>
                            {(() => {
                              const st = getListingStatus(entry);
                              const canEdit = !['Expired', 'Accepted', 'Rejected'].includes(st);
                              return canEdit ? (
                                <button
                                  className="btn-manage-bids-detailed"
                                  style={{ background: '#f59e0b', fontSize: '0.68rem', padding: '4px 8px' }}
                                  onClick={() => handleStartEdit(entry)}
                                  title="Edit this listing"
                                >
                                  ✏ Edit
                                </button>
                              ) : null;
                            })()}
                            <button className={`btn-manage-bids-detailed ${hasUnseenBids(entry.id) ? 'blinking-btn' : ''}`} onClick={() => setManagedListingId(entry.id)}>View Bids</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        )}
      </main>

      {showTerms && (
        <div className="modal-overlay-blur">
          <div className="modal-card-premium" style={{ maxWidth: '600px' }}>
            <div className="card-header-flex">
              <h3>Platform Terms & Conditions</h3>
              <button className="close-x" onClick={() => setShowTerms(false)}>×</button>
            </div>
            <div style={{ padding: '20px', fontSize: '0.9rem', lineHeight: '1.6', color: '#475569' }}>
              <p>1. <strong>Binding Offers:</strong> All listings published on GoEasyTrade are considered binding for the valid date and time specified.</p>
              <p>2. <strong>Quality Assurance:</strong> Sellers are responsible for the accuracy of technical parameters (SCI, Mic, etc.) provided.</p>
              <p>3. <strong>Dispute Resolution:</strong> Any discrepancies in quality upon delivery must be reported within 48 hours for arbitration.</p>
              <p>4. <strong>Platform Fees:</strong> Standard platform commission rates apply upon successful bid acceptance.</p>
            </div>
            <div style={{ textAlign: 'right', padding: '15px 25px', borderTop: '1px solid #e2e8f0' }}>
              <button className="btn-save-listing-large compact-btn" onClick={() => setShowTerms(false)}>Understood</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerDomesticHvi;
